import {memo} from 'react';

import styled from 'styled-components';

import CircleTimer from '~/shared/components/CircleTimer';
import {getLocalizationService} from '~/shared/services/localisationService';
import {bodyBold, smallBold, smallRegular} from '~/shared/theme/typography';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import PieButton from '~/shared/theme/pie/PieButton';
import AlertCircleIcon from '~/assets/images/icons/alertCircle.svg?react';

const ContentOverlay = styled.div<{isWalle?: boolean}>`
  position: absolute;
  inset: 0;
  z-index: 2;
  padding: 4.88% 4.88% 6.1%;
  display: flex;
  flex-direction: column;
  justify-content: ${({isWalle}) => (isWalle ? 'flex-end' : 'space-between')};
  ${({isWalle}) => (isWalle && 'gap: 4px;')};
`;

const CardHolderNameContainer = styled.div`
  display: flex;
  justify-content: left;
`;

const CardHolderName = styled.span`
  ${bodyBold}
  display: block;
  color: ${({theme}) => theme.colors.surface};
`;

const BottomBlock = styled.div`
  direction: ltr;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardDetailsWrapper = styled(FlexColumn)`
  gap: 4px;
`;

const CardDetail = styled.span<{isActive?: boolean; isWalle?: boolean}>`
  ${smallRegular}
  display: block;
  color: ${({isActive, isWalle, theme}) => theme.colors[!isActive ? 'contentPlaceholder' : isWalle ? 'surface' : 'contentDefault']};
`;

const StyledPieButton = styled(PieButton)`
  ${smallBold}

  .button-content-container {
    gap: 8px;
  }
`;

const ExpiryAndCvvWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SeparatorDot = styled.div<{isWalle?: boolean}>`
  border-radius: 50%;
  width: 3px;
  height: 3px;
  background-color: ${({theme, isWalle}) => theme.colors[isWalle ? 'surface' : 'contentDisabled']};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  direction: ${({theme}) => theme.direction};
`;

const ErrorText = styled.span`
  ${smallRegular}
  display: block;
  color: ${({theme}) => theme.colors.contentError};
`;

type CardBaseProps = {
  isWalle?: boolean;
  cardHolderName: string;
  hasError: boolean;
  isActive: boolean;
  isRevealed: boolean;
  isLoading: boolean;
  cardNumber: string;
  cvv?: string;
  expiry?: string;
  showDetailsHandler: () => Promise<void>;
  onActivateCardClick?: () => void;
};

const CardBase = ({
  isWalle,
  cardHolderName,
  hasError,
  isActive,
  isRevealed,
  isLoading,
  cardNumber,
  cvv,
  expiry,
  showDetailsHandler,
  onActivateCardClick,
}: CardBaseProps) => {
  const {t} = getLocalizationService();

  return (
    <ContentOverlay isWalle={isWalle}>
      <CardHolderNameContainer>
        <CardHolderName>{cardHolderName}</CardHolderName>
      </CardHolderNameContainer>
      <BottomBlock>
        <CardDetailsWrapper>
          {!hasError && (
            <>
              <CardDetail isWalle={isWalle} isActive={isActive}>{cardNumber}</CardDetail>
              {(cvv || expiry) && (
                <ExpiryAndCvvWrapper>
                  <CardDetail isWalle={isWalle} isActive={isActive}>{expiry}</CardDetail>
                  {cvv && expiry && <SeparatorDot isWalle={isWalle} />}
                  <CardDetail isWalle={isWalle} isActive={isActive}>{cvv}</CardDetail>
                </ExpiryAndCvvWrapper>
              )}
            </>
          )}
          {hasError && (
            <ErrorContainer>
              <ErrorText>{t('failed_to_load_details')}</ErrorText>
              <AlertCircleIcon />
            </ErrorContainer>
          )}
        </CardDetailsWrapper>
        {isActive && (
          <StyledPieButton
            variant="outlineMedium"
            onClick={showDetailsHandler}
            isLoading={isLoading}
          >
            {isRevealed && <CircleTimer timeoutSeconds={60} size={15} strokeWidth={1.5} reversed />}
            {t(isRevealed ? 'hide' : 'show_details')}
          </StyledPieButton>
        )}
        {!isActive && (
          <StyledPieButton variant="primaryMedium" onClick={onActivateCardClick}>{t('activate_card2')}</StyledPieButton>
        )}
      </BottomBlock>
    </ContentOverlay>
  );
};

export default memo(CardBase);
